import { LOOKS } from "./assetManifest";
import { SPAWN_CATEGORY } from "./spawnCategories";
import {
    GRADES, MODES, SURFACES, ZONES,
    type DensityParams, type GameConfig, type PlayerConfig, type SurfaceConfig,
} from "./types";

type Range = [min: number, max: number];

const PLAYER_RANGES: Record<Exclude<keyof PlayerConfig, "crashDurationS">, Range> = {
    startSpeed: [0, 100],
    maxSpeed: [1, 100],
    boostSpeed: [1, 150],
    boostAccel: [0, 50],
    gravityAccel: [0, 50],
    pedalAccel: [0, 50],
    pedalCapBonus: [0, 30],
    braking: [0, 100],
    skidThreshold: [0, 100],
    skidBraking: [0, 100],
    skidSteerFactor: [0, 1],
    turnRate: [1, 720],
    headingSmoothing: [0, 50],
    traverseFriction: [0, 50],
    minRollSpeed: [0, 50],
    overCapDecay: [0, 20],
    sharpTurnLoss: [0, 1],
    boostDrainPerS: [0, 10],
    boostRefillCleanLanding: [0, 1],
    boostRefillPerTrickPoint: [0, 1],
    boostPassiveRefillPerS: [0, 1],
    invulnerableS: [0, 10],
};

const SURFACE_RANGES: Record<keyof Omit<SurfaceConfig, "spray" | "sound" | "haptic" | "turnLoss">, Range> = {
    grip: [0, 1],
    speedMod: [0.1, 2],
    drag: [0, 20],
    rough: [0, 5],
};

const DENSITY_KEYS: Record<keyof DensityParams, true> = {
    treeDensity: true, rockDensity: true, dogDensity: true, hikerDensity: true,
    npcRiderDensity: true, wildlifeDensity: true, jumpFrequency: true, regionFrequency: true,
    gateFrequency: true, pickupFrequency: true, setPieceWeight: true,
};

class Checker {
    errors: string[] = [];

    fail(message: string) {
        this.errors.push(message);
    }

    num(path: string, value: unknown, [min, max]: Range = [0, Infinity]) {
        if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
            this.fail(`${path} should be a number in [${min}, ${max}], got ${JSON.stringify(value)}`);
        }
    }

    oneOf<T>(path: string, value: T, allowed: readonly T[]) {
        if (!allowed.includes(value)) this.fail(`${path} is "${value}", expected one of: ${allowed.join(", ")}`);
    }

    look(path: string, key: string) {
        if (!(key in LOOKS)) this.fail(`${path} "${key}" is not in the asset manifest`);
    }
}

function checkPlayer(c: Checker, { player }: GameConfig) {
    for (const [key, range] of Object.entries(PLAYER_RANGES)) {
        c.num(`player.${key}`, player[key as keyof typeof PLAYER_RANGES], range);
    }
    for (const severity of ["small", "medium", "large"] as const) {
        c.num(`player.crashDurationS.${severity}`, player.crashDurationS?.[severity], [0, 10]);
    }
    if (!(player.startSpeed < player.maxSpeed && player.maxSpeed < player.boostSpeed)) {
        c.fail("player speeds should rise: startSpeed < maxSpeed < boostSpeed");
    }
}

function checkZones(c: Checker, { zones }: GameConfig) {
    for (const id of SURFACES) {
        const surface = zones.surfaces[id];
        if (!surface) c.fail(`zones.surfaces.${id} is missing`);
        else for (const [key, range] of Object.entries(SURFACE_RANGES)) {
            c.num(`zones.surfaces.${id}.${key}`, surface[key as keyof typeof SURFACE_RANGES], range);
        }
    }
    for (const id of ZONES) {
        const zone = zones.zones[id];
        if (!zone) {
            c.fail(`zones.zones.${id} is missing`);
            continue;
        }
        c.oneOf(`zones.${id}.baseSurface`, zone.baseSurface, SURFACES);
        if (zone.marksKey) c.look(`zones.${id}.marksKey`, zone.marksKey);
        if (zone.trail) {
            c.oneOf(`zones.${id}.trail.surface`, zone.trail.surface, SURFACES);
            c.num(`zones.${id}.trail.width`, zone.trail.width, [0.5, 20]);
            c.num(`zones.${id}.trail.wiggle`, zone.trail.wiggle, [0, 40]);
        }
        zone.spawnTable.forEach((entry, i) => {
            c.look(`zones.${id}.spawnTable[${i}].key`, entry.key);
            if (!(entry.key in SPAWN_CATEGORY)) c.fail(`zones.${id}.spawnTable[${i}].key "${entry.key}" has no spawn category`);
            c.num(`zones.${id}.spawnTable[${i}].weight`, entry.weight, [0.01, 100]);
        });
    }
}

function checkCourses(c: Checker, { courses }: GameConfig) {
    const ids = courses.courses.map((course) => course.id);
    if (ids.length !== new Set(ids).size) c.fail("courses have duplicate ids");

    for (const course of courses.courses) {
        const path = `courses[${course.id}]`;
        c.oneOf(`${path}.id`, course.id, [1, 2, 3]);
        c.oneOf(`${path}.grade`, course.grade, GRADES);
        c.num(`${path}.seed`, course.seed);
        c.num(`${path}.bigfoot.aggression`, course.bigfoot.aggression, [0, 1]);
        if (!course.legs.length) c.fail(`${path} has no zone legs`);
        course.legs.forEach((leg, i) => {
            c.oneOf(`${path}.legs[${i}].zone`, leg.zone, ZONES);
            c.num(`${path}.legs[${i}].lengthM`, leg.lengthM, [1, 100_000]);
        });
        if (!course.legs.some((leg) => leg.zone === course.bigfoot.zone)) {
            c.fail(`${path}.bigfoot.zone "${course.bigfoot.zone}" is not one of the course's zones`);
        }
    }
    courses.freeride.cycle.forEach((leg, i) => {
        c.oneOf(`courses.freeride.cycle[${i}].zone`, leg.zone, ZONES);
        c.num(`courses.freeride.cycle[${i}].lengthM`, leg.lengthM, [1, 100_000]);
    });
    c.num("courses.freeride.bigfootAtM", courses.freeride.bigfootAtM);
    c.num("courses.freeride.aggression", courses.freeride.aggression, [0, 1]);
}

function checkObstacles(c: Checker, { obstacles }: GameConfig) {
    for (const [key, obstacle] of Object.entries(obstacles)) {
        c.look("obstacles", key);
        c.oneOf(`obstacles.${key}.kind`, obstacle.kind, ["solid", "soft", "region", "decor", "ramp"]);
        if (obstacle.kind === "ramp" && obstacle.dropM === undefined) c.num(`obstacles.${key}.launchScale`, obstacle.launchScale, [0.1, 5]);
        if (obstacle.kind === "region") c.oneOf(`obstacles.${key}.surface`, obstacle.surface, SURFACES);
        if (obstacle.kind === "solid") {
            c.num(`obstacles.${key}.heightPx`, obstacle.heightPx, [1, 2000]);
            c.num(`obstacles.${key}.crashAtSpeed.medium`, obstacle.crashAtSpeed?.medium);
            c.num(`obstacles.${key}.crashAtSpeed.large`, obstacle.crashAtSpeed?.large);
        }
        obstacle.hitboxPx?.forEach((n, i) => c.num(`obstacles.${key}.hitboxPx[${i}]`, n, [1, 2000]));
        if (obstacle.speedLoss !== undefined) c.num(`obstacles.${key}.speedLoss`, obstacle.speedLoss, [0, 1]);
    }
}

function checkDifficulty(c: Checker, { difficulty }: GameConfig) {
    for (const zone of ZONES) {
        const base = difficulty.zoneBase[zone];
        if (!base) c.fail(`difficulty.zoneBase.${zone} is missing`);
        else for (const key of Object.keys(DENSITY_KEYS)) {
            c.num(`difficulty.zoneBase.${zone}.${key}`, base[key as keyof DensityParams]);
        }
    }
    for (const key of Object.keys(DENSITY_KEYS)) {
        c.num(`difficulty.caps.${key}`, difficulty.caps[key as keyof DensityParams]);
    }
    for (const grade of GRADES) c.num(`difficulty.gradeMultiplier.${grade}`, difficulty.gradeMultiplier[grade], [0.1, 5]);
    difficulty.freeride.bands.forEach((band, i) => {
        c.num(`difficulty.freeride.bands[${i}].fromM`, band.fromM);
        c.num(`difficulty.freeride.bands[${i}].scale`, band.scale, [0, 10]);
    });
}

function checkTricks(c: Checker, { tricks }: GameConfig) {
    for (const [id, trick] of Object.entries(tricks.tricks)) {
        c.num(`tricks.${id}.value`, trick.value);
    }
    for (const [key, value] of Object.entries(tricks.air)) c.num(`tricks.air.${key}`, value);
    c.num("tricks.trickDuration", tricks.trickDuration, [0.1, 3]);
    c.num("tricks.cleanLandingAngle", tricks.cleanLandingAngle, [0, 90]);
}

function checkNumbers(c: Checker, path: string, value: unknown) {
    if (typeof value === "number") c.num(path, value);
    else if (value && typeof value === "object") {
        for (const [key, child] of Object.entries(value)) checkNumbers(c, `${path}.${key}`, child);
    }
}

function checkHazards(c: Checker, { hazards, zones }: GameConfig) {
    checkNumbers(c, "hazards", hazards);
    for (const [id, zone] of Object.entries(zones.zones)) {
        zone.spawnTable.forEach((entry, i) => {
            const poses = LOOKS[entry.key as keyof typeof LOOKS]?.poses ?? [];
            if (entry.variant && !poses.includes(entry.variant)) {
                c.fail(`zones.${id}.spawnTable[${i}].variant "${entry.variant}" is not a pose of ${entry.key}`);
            }
        });
    }
}

function checkScoring(c: Checker, { scoring }: GameConfig) {
    checkNumbers(c, "scoring", scoring);
    c.num("scoring.comboCap", scoring.comboCap, [1, 100]);
}

function checkModes(c: Checker, { modes }: GameConfig) {
    for (const id of MODES) {
        const mode = modes.modes[id];
        if (!mode) {
            c.fail(`modes.${id} is missing`);
            continue;
        }
        c.num(`modes.${id}.gates`, mode.gates, [0, 200]);
        c.num(`modes.${id}.gateMissPenaltyS`, mode.gateMissPenaltyS, [0, 60]);
        for (const [key, value] of Object.entries(mode.difficultyScale)) {
            if (!(key in DENSITY_KEYS)) c.fail(`modes.${id}.difficultyScale.${key} is not a difficulty param`);
            c.num(`modes.${id}.difficultyScale.${key}`, value, [0, 10]);
        }
    }
}

export function validateConfig(config: GameConfig) {
    const c = new Checker();
    for (const check of [checkPlayer, checkZones, checkCourses, checkObstacles, checkDifficulty, checkTricks, checkModes, checkHazards, checkScoring]) {
        check(c, config);
    }
    if (c.errors.length) throw new Error(`Invalid config:\n- ${c.errors.join("\n- ")}`);
}
